<template>
  <div>
    <!-- Back navigation -->
    <div class="flex items-center gap-3 mb-6">
      <button
        @click="$router.back()"
        class="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-custom-blue"
      >
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Back to Patients
      </button>
      <span class="text-gray-300">|</span>
      <h2 class="text-lg font-semibold text-gray-700">Medical History</h2>
    </div>

    <!-- Visit list loading -->
    <div v-if="loadingVisits" class="flex justify-center items-center py-16">
      <svg class="animate-spin h-8 w-8 text-custom-yellow" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
    </div>

    <!-- Patient details banner -->
    <div v-if="patient" class="mb-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div class="flex items-start justify-between">
        <div>
          <p class="text-xl font-bold text-gray-900">{{ patient.full_name }}</p>
          <p class="text-sm text-gray-500 mt-1">
            {{ patient.gender }} &bull; {{ patient.age }} yrs &bull; DOB: {{ patient.dob }}
          </p>
          <p v-if="patient.phone_number" class="text-sm text-gray-500 mt-0.5">
            {{ patient.phone_number }}
          </p>
        </div>
        <div class="flex flex-col items-end gap-1.5">
          <span class="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded border border-gray-200">
            {{ patient.unique_code }}
          </span>
          <span v-if="patient.blood_group"
            class="text-xs px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded">
            {{ patient.blood_group }}
          </span>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="visits.length === 0" class="text-center py-16 text-gray-400">
      <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0121 9.414V19a2 2 0 01-2 2z" />
      </svg>
      <p class="font-medium">No visits found for this patient.</p>
    </div>


    <!-- Visit accordion -->
    <div v-else class="space-y-3">
      <div
        v-for="visit in visits"
        :key="visit.id"
        class="border border-gray-200 rounded-lg overflow-hidden shadow-sm"
      >
        <!-- Card header — always visible, clickable -->
        <div
          @click="toggleVisit(visit)"
          class="flex items-center justify-between px-5 py-4 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <!-- Left: visit info -->
          <div class="flex items-center gap-4">
            <div>
              <span class="font-semibold text-gray-800 text-sm">{{ visit.visit_code }}</span>
              <span class="text-gray-400 text-xs ml-2">{{ formatDate(visit.visit_start_time) }}</span>
            </div>
            <span class="text-xs px-2 py-1 rounded border border-gray-300 bg-gray-100 text-gray-600">
              {{ visit.visit_type }}
            </span>
          </div>

          <!-- Middle: stage completion badges -->
          <div class="flex items-center gap-1.5">
            <span v-if="visit.stages_completed.triage"
              class="text-xs px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">
              Triage
            </span>
            <span v-if="visit.stages_completed.consultation"
              class="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
              Consultation
            </span>
            <span v-if="visit.stages_completed.lab"
              class="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
              Lab
            </span>
            <span v-if="visit.stages_completed.radiology"
              class="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
              Radiology
            </span>
            <span v-if="visit.stages_completed.pharmacy"
              class="text-xs px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 border border-pink-200">
              Pharmacy
            </span>
          </div>

          <!-- Right: print + chevron -->
          <div class="flex items-center gap-3 flex-shrink-0">
            <button
              @click.stop="printVisit(visit)"
              :disabled="printingVisitId === visit.id"
              class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              title="Print / Export this visit as PDF"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {{ printingVisitId === visit.id ? 'Preparing...' : 'Print / Export' }}
            </button>
            <svg
              class="w-5 h-5 text-gray-400 transition-transform duration-200"
              :class="{ 'rotate-180': expandedVisitId === visit.id }"
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <!-- Card body — only rendered when this visit is expanded -->
        <div v-if="expandedVisitId === visit.id" class="border-t border-gray-200">

          <!-- Detail loading state -->
          <div v-if="loadingDetail[visit.id]" class="flex justify-center items-center py-8">
            <svg class="animate-spin h-6 w-6 text-custom-yellow" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span class="ml-3 text-sm text-gray-500">Loading visit details...</span>
          </div>

          <!-- Visit detail with stage tabs -->
          <div v-else-if="visitDetails[visit.id]">

            <!-- Stage tab navigation -->
            <div class="flex gap-1 px-5 pt-4 border-b border-gray-100">
              <button
                v-for="stage in getAvailableStages(visit)"
                :key="stage.key"
                @click="setActiveStage(visit.id, stage.key)"
                class="px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors"
                :class="activeStage[visit.id] === stage.key
                  ? 'border-custom-yellow text-custom-blue bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'"
              >
                {{ stage.label }}
              </button>
            </div>

            <div class="p-5">
              <TriageStageView
                v-if="activeStage[visit.id] === 'triage'"
                :detail="visitDetails[visit.id]"
                @detail-updated="refreshVisitDetail(visit.id)"
              />
              <ConsultationStageView
                v-if="activeStage[visit.id] === 'consultation'"
                :detail="visitDetails[visit.id]"
                @detail-updated="refreshVisitDetail(visit.id)"
              />
              <LabStageView
                v-if="activeStage[visit.id] === 'lab'"
                :detail="visitDetails[visit.id]"
                @detail-updated="refreshVisitDetail(visit.id)"
              />
              <RadiologyStageView
                v-if="activeStage[visit.id] === 'radiology'"
                :detail="visitDetails[visit.id]"
                @detail-updated="refreshVisitDetail(visit.id)"
              />
              <PharmacyStageView
                v-if="activeStage[visit.id] === 'pharmacy'"
                :detail="visitDetails[visit.id]"
              />
            </div>
          </div>

          <!-- Detail fetch error -->
          <div v-else class="px-5 py-6 text-sm text-red-500">
            Failed to load visit details.
            <button @click="loadVisitDetail(visit.id)" class="ml-2 underline">Retry</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/api'
import dayjs from 'dayjs'
import TriageStageView       from '@/components/medical_history/TriageStageView.vue'
import ConsultationStageView from '@/components/medical_history/ConsultationStageView.vue'
import LabStageView          from '@/components/medical_history/LabStageView.vue'
import RadiologyStageView    from '@/components/medical_history/RadiologyStageView.vue'
import PharmacyStageView     from '@/components/medical_history/PharmacyStageView.vue'

const STAGE_CONFIG = [
  { key: 'triage',       label: 'Triage' },
  { key: 'consultation', label: 'Consultation' },
  { key: 'lab',          label: 'Lab' },
  { key: 'radiology',    label: 'Radiology' },
  { key: 'pharmacy',     label: 'Pharmacy' },
]

export default {
  name: 'MedicalHistoryVisitList',

  components: {
    TriageStageView,
    ConsultationStageView,
    LabStageView,
    RadiologyStageView,
    PharmacyStageView,
  },

  props: {
    patientId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      visits:          [],
      patient:         null,
      loadingVisits:   true,
      expandedVisitId: null,
      visitDetails:    {},
      loadingDetail:   {},
      activeStage:     {},
      printingVisitId: null,
      facilityDetails: null,
    }
  },

  mounted() {
    this.fetchVisits()
  },

  methods: {
    async fetchVisits() {
      try {
        this.loadingVisits = true
        const response = await apiClient.getPatientMedicalHistory(this.patientId)
        console.log('patient response', response)
        if (response.success) {
          this.visits = response.data.visits
          this.patient = response.data.patient
        } else {
          this.$toast.error('Failed to load visit history')
        }
      } catch {
        this.$toast.error('Failed to load visit history')
      } finally {
        this.loadingVisits = false
      }
    },

    async toggleVisit(visit) {
      if (this.expandedVisitId === visit.id) {
        this.expandedVisitId = null
        return
      }

      this.expandedVisitId = visit.id

      if (!this.activeStage[visit.id]) {
        const firstStage = this.getAvailableStages(visit)[0]
        if (firstStage) {
          this.activeStage[visit.id] = firstStage.key
        }
      }

      if (!this.visitDetails[visit.id]) {
        await this.loadVisitDetail(visit.id)
      }
    },

    async loadVisitDetail(visitId) {
      try {
        this.loadingDetail[visitId] = true
        const response = await apiClient.getVisitEMRDetail(visitId)
        if (response.success) {
          this.visitDetails[visitId] = response.data
        } else {
          this.$toast.error('Failed to load visit details')
        }
      } catch {
        this.$toast.error('Failed to load visit details')
      } finally {
        this.loadingDetail[visitId] = false
      }
    },

    async refreshVisitDetail(visitId) {
      delete this.visitDetails[visitId]
      await this.loadVisitDetail(visitId)
    },


    setActiveStage(visitId, stageKey) {
      this.activeStage[visitId] = stageKey
    },

    getAvailableStages(visit) {
      return STAGE_CONFIG.filter(stage => visit.stages_completed[stage.key])
    },

    formatDate(dateString) {
      return dayjs(dateString).format('DD MMM YYYY, HH:mm')
    },

    esc(str) {
      if (str == null || str === '') return '—'
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },

    // Attribute-safe escape that doesn't fall back to '—' for empty values.
    escAttr(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },

    // `result.url` entries come from the backend as `{ url, filename }` objects,
    // but older records may still hold plain strings — support both.
    fileHref(file) {
      return typeof file === 'string' ? file : (file?.url || '')
    },

    fileName(file) {
      return typeof file === 'string' ? '' : (file?.filename || '')
    },

    isImageFile(file) {
      const name = this.fileName(file) || this.fileHref(file)
      return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name)
    },

    // ── Print / Export a single visit as PDF (via the browser's print dialog) ──
    // Same technique already used for prescriptions (PrescriptionTab.vue): build
    // a hidden print-only container, isolate it with @media print, call
    // window.print(), then clean up. No new dependency needed — "Save as PDF"
    // in the print dialog covers the "export a PDF" half of the request.
    async printVisit(visit) {
      this.printingVisitId = visit.id
      try {
        if (!this.visitDetails[visit.id]) {
          await this.loadVisitDetail(visit.id)
        }
        const detail = this.visitDetails[visit.id]
        if (!detail) {
          this.$toast.error('Could not load this visit — nothing to print.')
          return
        }
        if (!this.facilityDetails) {
          await this.fetchFacilityDetails()
        }
        this.renderVisitPrint(visit, detail)
      } finally {
        this.printingVisitId = null
      }
    },

    async fetchFacilityDetails() {
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}')
        if (!stored?.FacilityId) return
        const response = await apiClient.fetchFacility(stored.FacilityId)
        if (response.success) this.facilityDetails = response.data
      } catch {
        // Facility signature is a header nicety — silently skip if it can't be fetched.
      }
    },

    renderVisitPrint(visit, detail) {
      const esc = this.esc.bind(this)
      const stored = JSON.parse(localStorage.getItem('user') || '{}')
      const facilityName = this.facilityDetails?.FacilityName || stored?.FacilityName || 'JiJi Health'
      const facilityLocation = [this.facilityDetails?.NearestTown, this.facilityDetails?.County].filter(Boolean).join(', ')
      const facilityLogoHtml = this.facilityDetails?.logo_url
        ? `<img src="${this.facilityDetails.logo_url}" style="max-height:70px;max-width:150px;object-fit:contain;margin-bottom:6px;" alt="Logo" />`
        : ''

      const vitals     = detail.triage_vitals?.[0] || null
      const assessment = detail.triage_assesment || null
      const gcs        = detail.triage_glasgow_coma_scale || null
      const complaint  = detail.complaint || null
      const consult    = detail.consultation || null
      const physExam   = detail.physical_exam || null
      const diagnoses  = detail.diagnosis || []
      const prelim     = diagnoses.filter(d => d.diagnosis_type === 1)
      const final      = diagnoses.filter(d => d.diagnosis_type === 2)
      const prescriptions = (detail.prescriptions || []).filter(p => p.status?.code !== 'ST-019')
      const labTests       = detail.lab_tests || []
      const radiologyTests = detail.radiology_tests || []
      const procedures     = detail.procedures || []

      const sectionTitle = (title) => `
        <h2 style="background:#1a365d;color:#fff;padding:6px 12px;margin:18px 0 10px 0;font-size:11.5pt;border-radius:3px;">${esc(title)}</h2>`

      const infoGrid = (pairs) => `
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;font-size:10pt;margin-bottom:8px;">
          ${pairs.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([label, value]) => `
            <div><strong>${esc(label)}:</strong> ${esc(value)}</div>`).join('')}
        </div>`

      // ── Triage ─────────────────────────────────────────────────────────────
      let triageHtml = ''
      if (vitals || assessment || gcs) {
        triageHtml += sectionTitle('Triage')
        if (vitals) {
          triageHtml += infoGrid([
            ['Temperature', vitals.temperature ? `${vitals.temperature} °C` : null],
            ['Heart Rate', vitals.heart_rate ? `${vitals.heart_rate} bpm` : null],
            ['Blood Pressure', (vitals.blood_pressure_systolic && vitals.blood_pressure_diastolic) ? `${vitals.blood_pressure_systolic}/${vitals.blood_pressure_diastolic} mmHg` : null],
            ['Respiratory Rate', vitals.respiratory_rate ? `${vitals.respiratory_rate} /min` : null],
            ['Oxygen Saturation', vitals.oxygen_saturation ? `${vitals.oxygen_saturation}%` : null],
            ['Weight', vitals.weight ? `${vitals.weight} kg` : null],
            ['Height', vitals.height ? `${vitals.height} cm` : null],
            ['BMI', vitals.bmi],
          ])
        }
        if (assessment) {
          triageHtml += infoGrid([
            ['Reason for Visit', assessment.visit_reason],
            ['Chief Complaint', assessment.complaint],
            ['Complaint Duration', assessment.complaint_duration ? `${assessment.complaint_duration} days` : null],
            ['Pain Level', assessment.pain_level != null ? `${assessment.pain_level} / 10` : null],
          ])
        }
        if (gcs) {
          triageHtml += infoGrid([['Glasgow Coma Scale', gcs.score]])
        }
      }

      // ── Consultation ───────────────────────────────────────────────────────
      let consultHtml = ''
      if (complaint || consult || physExam || diagnoses.length) {
        consultHtml += sectionTitle('Consultation')
        if (complaint) {
          const symptoms = complaint.symptoms?.length
            ? complaint.symptoms.map(s => s.chief_complaint).filter(Boolean).join(', ')
            : complaint.chief_complaint
          consultHtml += infoGrid([
            ['Presenting Complaint', symptoms],
            ['History of Illness', complaint.history_of_illness],
          ])
        }
        if (physExam) {
          consultHtml += infoGrid([
            ['General', physExam.general], ['Respiratory', physExam.respiratory_system],
            ['Cardiovascular', physExam.cadiovascular_system], ['Abdomen', physExam.abdomen],
            ['Genitourinary', physExam.genitourinary], ['ENT', physExam.ent],
            ['Skin', physExam.skin], ['Musculoskeletal', physExam.musculoskeletal],
            ['CNS', physExam.cns],
          ])
        }
        if (prelim.length) {
          consultHtml += infoGrid([['Preliminary Diagnosis', prelim.map(d => d.diagnosis?.name).filter(Boolean).join(', ')]])
        }
        if (final.length) {
          consultHtml += infoGrid([['Final Diagnosis', final.map(d => d.diagnosis?.name).filter(Boolean).join(', ')]])
        }
        if (consult?.findings) {
          consultHtml += infoGrid([['Findings', consult.findings]])
        }
      }

      // ── Prescriptions ──────────────────────────────────────────────────────
      let prescriptionsHtml = ''
      if (prescriptions.length) {
        prescriptionsHtml = sectionTitle('Prescriptions') + `
          <table style="width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:10px;">
            <thead><tr>
              ${['Drug', 'Dose', 'Frequency', 'Duration', 'Route', 'Notes'].map(h =>
                `<th style="background:#f3f4f6;padding:5px 7px;text-align:left;border-bottom:1px solid #ccc;">${h}</th>`).join('')}
            </tr></thead>
            <tbody>
              ${prescriptions.map(p => `<tr>
                <td style="padding:4px 7px;border-bottom:1px solid #eee;"><strong>${esc(p.inventory_item?.trade_name)}</strong></td>
                <td style="padding:4px 7px;border-bottom:1px solid #eee;">${esc(p.dose)}</td>
                <td style="padding:4px 7px;border-bottom:1px solid #eee;">${esc(p.inventory_dosage_frequency?.name)}</td>
                <td style="padding:4px 7px;border-bottom:1px solid #eee;">${p.days ? esc(p.days) + ' days' : '—'}</td>
                <td style="padding:4px 7px;border-bottom:1px solid #eee;">${esc(p.administration_route?.name)}</td>
                <td style="padding:4px 7px;border-bottom:1px solid #eee;">${esc(p.notes)}</td>
              </tr>`).join('')}
            </tbody>
          </table>`
      }

      // ── Laboratory ─────────────────────────────────────────────────────────
      let labHtml = ''
      if (labTests.length) {
        labHtml = sectionTitle('Laboratory')
        labTests.forEach(t => {
          const latest = t.lab_results?.length ? [...t.lab_results].reverse()[0] : null
          labHtml += `
            <div style="margin-bottom:8px;padding:8px 10px;border:1px solid #e5e7eb;border-radius:4px;">
              <p style="margin:0 0 4px 0;font-size:10pt;"><strong>${esc(t.test_type?.name)}</strong> — ${esc(t.status?.name)}</p>
              ${latest
                ? `<p style="margin:2px 0;font-size:9.5pt;">${esc(latest.results)}</p>
                   ${latest.comments ? `<p style="margin:2px 0;font-size:9pt;color:#666;font-style:italic;">${esc(latest.comments)}</p>` : ''}`
                : `<p style="margin:2px 0;font-size:9pt;color:#999;">No result recorded yet.</p>`}
            </div>`
        })
      }

      // ── Radiology ──────────────────────────────────────────────────────────
      let radiologyHtml = ''
      if (radiologyTests.length) {
        radiologyHtml = sectionTitle('Radiology')
        radiologyTests.forEach(t => {
          const latest = t.radiology_results?.length ? [...t.radiology_results].reverse()[0] : null
          const attachments = latest?.url?.length
            ? `<div style="margin-top:4px;display:flex;flex-wrap:wrap;gap:6px;">
                ${latest.url.map(file => this.isImageFile(file)
                  ? `<img src="${this.escAttr(this.fileHref(file))}" style="height:70px;width:70px;object-fit:cover;border:1px solid #e5e7eb;border-radius:3px;" />`
                  : `<a href="${this.escAttr(this.fileHref(file))}" style="font-size:9pt;color:#2563eb;">${esc(this.fileName(file) || 'Attachment')}</a>`
                ).join('')}
               </div>`
            : ''
          radiologyHtml += `
            <div style="margin-bottom:8px;padding:8px 10px;border:1px solid #e5e7eb;border-radius:4px;">
              <p style="margin:0 0 4px 0;font-size:10pt;"><strong>${esc(t.imaging_type?.name)}</strong> — ${esc(t.status?.name)}</p>
              ${latest
                ? `${latest.findings ? `<p style="margin:2px 0;font-size:9.5pt;"><strong>Findings:</strong> ${esc(latest.findings)}</p>` : ''}
                   ${latest.recommendation ? `<p style="margin:2px 0;font-size:9.5pt;"><strong>Recommendation:</strong> ${esc(latest.recommendation)}</p>` : ''}
                   ${latest.conclusion ? `<p style="margin:2px 0;font-size:9.5pt;"><strong>Conclusion:</strong> ${esc(latest.conclusion)}</p>` : ''}
                   ${attachments}`
                : `<p style="margin:2px 0;font-size:9pt;color:#999;">No result recorded yet.</p>`}
            </div>`
        })
      }

      // ── Procedures ─────────────────────────────────────────────────────────
      let proceduresHtml = ''
      if (procedures.length) {
        proceduresHtml = sectionTitle('Procedures')
        procedures.forEach(p => {
          const r = p.procedure_result
          proceduresHtml += `
            <div style="margin-bottom:8px;padding:8px 10px;border:1px solid #e5e7eb;border-radius:4px;">
              <p style="margin:0 0 4px 0;font-size:10pt;"><strong>${esc(p.facility_service?.name)}</strong> — ${esc(p.status?.name)}</p>
              ${r?.findings ? `<p style="margin:2px 0;font-size:9.5pt;"><strong>Findings:</strong> ${esc(r.findings)}</p>` : ''}
            </div>`
        })
      }

      const patient = detail.patient || {}
      const printHtml = `
        <div style="font-family:'Georgia','Times New Roman',serif;font-size:10.5pt;color:#111;padding:16mm 20mm;max-width:210mm;margin:0 auto;">
          <div style="border-bottom:3px double #1a365d;padding-bottom:10px;margin-bottom:14px;text-align:center;">
            ${facilityLogoHtml}
            <h1 style="font-size:18pt;font-weight:bold;color:#1a365d;margin:0 0 4px 0;">${esc(facilityName)}</h1>
            ${facilityLocation ? `<p style="font-size:9.5pt;color:#444;margin:0;">${esc(facilityLocation)}</p>` : ''}
            <div style="background:#1a365d;color:#fff;padding:4px 0;border-radius:4px;margin-top:8px;">
              <h2 style="margin:0;font-size:12pt;letter-spacing:3px;">VISIT RECORD</h2>
            </div>
          </div>

          ${infoGrid([
            ['Patient Name', patient.FullName], ['Patient Code', patient.unique_code],
            ['Gender', patient.Gender], ['Date of Birth', patient.Dob],
            ['Visit Code', visit.visit_code], ['Visit Type', visit.visit_type],
            ['Visit Date', this.formatDate(visit.visit_start_time)],
          ])}

          ${triageHtml}
          ${consultHtml}
          ${labHtml}
          ${radiologyHtml}
          ${proceduresHtml}
          ${prescriptionsHtml}

          <div style="margin-top:24px;border-top:1px solid #ccc;padding-top:8px;text-align:center;font-size:8.5pt;color:#666;">
            <p style="margin:0;">Printed ${dayjs().format('DD MMM YYYY, HH:mm')}</p>
            <p style="margin-top:4px;font-style:italic;color:#999;">Powered by JiJi Health &mdash; ${esc(facilityName)}</p>
          </div>
        </div>`

      const printContainer = document.createElement('div')
      printContainer.id = 'visit-print-container'
      printContainer.innerHTML = printHtml
      printContainer.style.cssText = 'position:fixed;top:-9999px;left:-9999px;visibility:hidden;'
      document.body.appendChild(printContainer)

      const printStyle = document.createElement('style')
      printStyle.id = 'visit-print-style'
      printStyle.textContent = `
        @media print {
          body > *:not(#visit-print-container) { display: none !important; }
          #visit-print-container {
            position: fixed !important;
            top: 0 !important; left: 0 !important;
            width: 100% !important;
            visibility: visible !important;
            background: white !important;
          }
        }
      `
      document.head.appendChild(printStyle)

      printContainer.style.visibility = ''
      window.print()

      document.body.removeChild(printContainer)
      document.head.removeChild(printStyle)
    },
  },
}
</script>

